'use client';

import { useEffect, useState } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Stack,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axiosClient from '../../api/axiosClient';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';

type EditEmployeeDrawerProps = {
  open: boolean;
  onClose: () => void;
  employeeId: number | null;
  onSuccess: () => void;
};

type Category = { id: number; name: string };

export default function EditEmployeeDrawer({
  open,
  onClose,
  employeeId,
  onSuccess,
}: EditEmployeeDrawerProps) {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    registrationNumber: '',
    startDate: '',
    endDate: '',
    onboardingDate: '',
    assignedCategoryId: 0,
    remoteWorkSurfaceArea: 0,
    teleworkRentElectricityTaxes: 0,
    teleworkConnection: 0,
    teleworkOffice: 0,
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [contractId, setContractId] = useState<number | null>(null);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const apiKey = localStorage.getItem('apiKey');
        if (!apiKey) return;

        const res = await axios.get(
          'https://staging-third-party-apis.nabil5332.workers.dev/api/v1/categories',
          { headers: { Authorization: `Bearer ${apiKey}` } }
        );

        setCategories(res.data.categories || []);
      } catch (err) {
        console.error('Erreur lors du chargement des catégories', err);
      }
    };
    fetchCategories();
  }, []);
  useEffect(() => {
    if (!employeeId || !open) return;

    const fetchEmployee = async () => {
      try {
        setFetching(true);
        setError(null);

        const res = await axiosClient.get(`employees/${employeeId}`);
        const data = res.data;

        setContractId(data.contract?.id || null);

        setForm({
          firstName: data.employee.firstName || '',
          lastName: data.employee.lastName || '',
          email: data.employee.email || '',
          phone: data.employee.phone || '',
          position: data.contract?.position || '',
          registrationNumber: data.contract?.registrationNumber || '',
          startDate: data.contract?.startDate?.split('T')[0] || '',
          endDate: data.contract?.endDate?.split('T')[0] || '',
          onboardingDate: data.contract?.onboardingDate?.split('T')[0] || '',
          assignedCategoryId:
            categories.find((c) => c.id === data.contract?.categoryId)?.id ||
            (categories[0]?.id ?? 0),
          remoteWorkSurfaceArea: data.contract?.remoteWorkSurfaceArea || 0,
          teleworkRentElectricityTaxes: data.contract?.rentUtilitiesTaxesPercentage || 0,
          teleworkConnection: data.contract?.internetConnectionPercentage || 0,
          teleworkOffice: data.contract?.officeEquipmentPercentage || 0,
        });

      } catch (err: any) {
        setError(err.response?.data?.error || 'Erreur lors du chargement de l’employé');
      } finally {
        setFetching(false);
      }
    };

    fetchEmployee();
  }, [employeeId, open, categories]);

  const handleChange = (field: string, value: string | number) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async () => {
    if (!employeeId) return;

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const employeePayload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        phone: form.phone,
        rentUtilitiesTaxesPercentage: Number(form.teleworkRentElectricityTaxes),
        internetConnectionPercentage: Number(form.teleworkConnection),
        officeEquipmentPercentage: Number(form.teleworkOffice),
      };


      await axiosClient.put(
        `employees/${employeeId}`,
        employeePayload,
        { headers: { 'Idempotency-Key': uuidv4() } }
      );


      if (contractId) {
        const contractPayload: any = {
          position: form.position,
          registrationNumber: form.registrationNumber,
          startDate: form.startDate || undefined,
          endDate: form.endDate || undefined,
          onboardingDate: form.onboardingDate || undefined,
          categoryId: form.assignedCategoryId,
          remoteWorkSurfaceArea: Number(form.remoteWorkSurfaceArea ?? 0),
        };


        await axiosClient.put(
          `employees/${employeeId}/contracts/${contractId}`,
          contractPayload,
          { headers: { 'Idempotency-Key': uuidv4() } }
        );
      }

      setSuccess('Employee & Contract updated successfully!');
      onSuccess();
      setTimeout(onClose, 800);

    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };


  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: 450, p: 4 } }}>
      <Box>
        <Stack direction="row" justifyContent="space-between" mb={2}>
          <Typography variant="h6">Edit Employee</Typography>
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        {fetching ? (
          <Box display="flex" justifyContent="center" mt={4}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={2}>
            <TextField label="First Name" value={form.firstName} onChange={(e) => handleChange('firstName', e.target.value)} fullWidth />
            <TextField label="Last Name" value={form.lastName} onChange={(e) => handleChange('lastName', e.target.value)} fullWidth />
            <TextField label="Email" value={form.email} onChange={(e) => handleChange('email', e.target.value)} fullWidth />
            <TextField label="Phone" value={form.phone} onChange={(e) => handleChange('phone', e.target.value)} fullWidth />
            <TextField label="Position" value={form.position} onChange={(e) => handleChange('position', e.target.value)} fullWidth />
            <TextField label="Matricule" value={form.registrationNumber} onChange={(e) => handleChange('registrationNumber', e.target.value)} fullWidth />

            <TextField
              select
              label="Catégorie"
              value={form.assignedCategoryId}
              onChange={(e) => handleChange('assignedCategoryId', Number(e.target.value))}
              fullWidth
            >
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>
              ))}
            </TextField>

            <TextField label="Onboarding Date" type="date" value={form.onboardingDate} onChange={(e) => handleChange('onboardingDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Start Date" type="date" value={form.startDate} onChange={(e) => handleChange('startDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="End Date" type="date" value={form.endDate} onChange={(e) => handleChange('endDate', e.target.value)} fullWidth InputLabelProps={{ shrink: true }} />
            <TextField label="Remote Work Surface Area" type="number" value={form.remoteWorkSurfaceArea} onChange={(e) => handleChange('remoteWorkSurfaceArea', Number(e.target.value))} fullWidth />

            {/* ✅ Section Télétravail */}
            <Typography variant="subtitle2" sx={{ mt: 3, fontWeight: 600 }}>
              Informations Télétravail
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Loyer / Électricité / Taxes (%)"
                type="number"
                value={form.teleworkRentElectricityTaxes}
                onChange={(e) => handleChange('teleworkRentElectricityTaxes', Number(e.target.value))}
                fullWidth
              />
              <TextField
                label="Connexion (%)"
                type="number"
                value={form.teleworkConnection}
                onChange={(e) => handleChange('teleworkConnection', Number(e.target.value))}
                fullWidth
              />
              <TextField
                label="Bureautique (%)"
                type="number"
                value={form.teleworkOffice}
                onChange={(e) => handleChange('teleworkOffice', Number(e.target.value))}
                fullWidth
              />
            </Stack>

            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
              {loading ? <CircularProgress size={20} /> : 'Save Changes'}
            </Button>
          </Stack>
        )}
      </Box>
    </Drawer>
  );
}
